import {
  Esrs_MaterialMetric_Insert_Input_,
  useGetAnswersForMetricsOnCompanyLevelQuery,
  useGetMetricReportStatusSubscription,
  useUpsertMaterialMetricsMutation,
  GetAnswersForMetricsOnCompanyLevelQuery_,
  GetAnswersForMetricsOnGroupLevelQuery_,
  GetReportMetricsPerDisclosureQuery_,
  QuestionType_Enum_,
  useGetReportGeneralDisclosureRequirementsQuery,
  TargetFieldsFragment_,
  useGetCompanyLevelMdrmAnswersQuery,
  GetCompanyLevelMdrmAnswersQuery_,
  GetAssessmentCurrencyConversionQuery_,
  useGetRequiredGroupMaterialMetricsQuery,
  useGetSubMaterialMetricsOnGroupLevelQuery,
  useGetSubAnswersOtherQuery,
  useGetSubAnswersEnvironmentQuery,
  useGetEsrsAssessmentShortDetailsQuery,
} from 'models';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  EsrsReportMetric,
  ReportAnswers,
  ReportChildMetrics,
  ReportCompanyAnswers,
  ReportMetric,
  ReportQueryMetric,
  SimplifiedReportNarrativeMetric,
} from './Report.types';
import { useAssessmentReportingUnits } from 'containers/Esrs/EsrsAssessment.hooks';
import {
  AggregatedMetricsTableData,
  MaybeHasChild,
  SubsidiaryAssessmentsType,
  getMaterialMetricsTags,
  getNestedRows,
  isFullMetricOnGroupLevel,
  isFullMetricOnSubLevel,
} from '../DisclosureRequirements';
import { getMetricMdrmAnswers, getMetricResult } from './MetricAnswerCalculation';
import { QuestionType } from 'utils/scores/questions';
import { useCompanyType } from 'utils/hooks';
import { ORDER_OF_CATEGORIES } from 'utils/categoriesOrder';
import { DataCollectionLevel } from '../DataCollection';
import { StructureNode } from '../Assessment/Structure';
import { uniq, uniqBy } from 'lodash';
import { toRomanNumeral } from './ReportUtils';
import { useGetEsrsReportStructure } from './ReportCloudFunctions.hooks';
import { environmentDRRefs, mfStandardRefs, percentageToNumber } from 'containers/Esrs/utils';
import { GroupMaterialMetrics } from '../DisclosureRequirements/Metrics/AggregatedMetrics/AggregatedMetricsUtils';
import { AssessableMetrics } from '../DisclosureRequirements/Metrics/Metrics';
import { E1_6_Target_Metrics, hardcodedMetrics } from './ReportComponents';
import { processReportTableData } from './Report.helpers';
import { sanitizeText } from './DocGenerator/report-helper-functions';
import { FrameworkRefs } from '../Assessment';

type ReportMaterialMetric =
  GetReportMetricsPerDisclosureQuery_['EsrsMetric'][number]['materialMetrics'][number];

export const filterQualitativeChildren = (metric: ReportMaterialMetric): ReportMaterialMetric => {
  const removeQualitativeChildren = (
    childMetric?: ReportMaterialMetric['childrenMetrics'][number]['childMetric']
  ): ReportMaterialMetric['childrenMetrics'][number]['childMetric'] | undefined => {
    if (childMetric?.childrenMetrics?.length) {
      return {
        ...childMetric,
        childrenMetrics:
          childMetric.childrenMetrics
            .filter((met) => met.childMetric?.metricType === QuestionType_Enum_.Decimal_)
            .map((met) => ({
              childMetric: removeQualitativeChildren(
                met.childMetric as ReportMaterialMetric['childrenMetrics'][number]['childMetric']
              ),
            })) ?? [],
      };
    }
    return childMetric;
  };
  return metric?.childrenMetrics?.length
    ? {
        ...metric,
        childrenMetrics: metric.childrenMetrics
          .filter((met) => met.childMetric?.metricType === QuestionType_Enum_.Decimal_)
          .map((met) => ({ childMetric: removeQualitativeChildren(met.childMetric) })),
      }
    : metric;
};

export const getLatestUpdated = (
  datapoints: ReportCompanyAnswers[number]['datapoints'] | undefined
) => {
  if (!datapoints?.length) return undefined;
  return datapoints?.reduce((lastUpdated, current) => {
    const currentDate = new Date(current.updatedAt);
    const latestDate = new Date(lastUpdated.updatedAt);
    return currentDate > latestDate ? current : lastUpdated;
  });
};

export const filterOptedOutRows = (
  metric: AggregatedMetricsTableData,
  optedOutAnswers: string[]
): AggregatedMetricsTableData => {
  const mapSubrows = (subRow?: AggregatedMetricsTableData): AggregatedMetricsTableData => {
    if (subRow?.subRows) {
      return {
        ...subRow,
        subRows: subRow.subRows
          .filter(
            (row) => !optedOutAnswers?.find((metricRef) => metricRef === row.metric.reference)
          )
          ?.map((sub) => mapSubrows(sub)),
      };
    }
    return {
      ...subRow,
      metric: subRow?.metric ?? ({} as AggregatedMetricsTableData['metric']),
    };
  };
  return {
    ...metric,
    subRows: metric.subRows
      ?.filter((row) => !optedOutAnswers?.find((metricRef) => metricRef === row.metric.reference))
      ?.map((subRow) => mapSubrows(subRow)),
  };
};

const addReportMaterialMetricTags = (
  metric: ReportMetric,
  groupAnswersData: SubsidiaryAssessmentsType[],
  standardRef: string
) => {
  let updatedMetric = {
    ...metric,
    materialMetrics: metric?.materialMetrics.map((materialMetric) => ({
      ...materialMetric,
      materialMetricTags: getMaterialMetricsTags(groupAnswersData, standardRef, metric.reference),
    })),
  };

  if (!!metric?.childrenMetrics?.length) {
    const updatedChildren: AssessableMetrics[number]['childrenMetrics'] =
      metric.childrenMetrics.map(({ childMetric }) => ({
        childMetric: childMetric
          ? addReportMaterialMetricTags(
              childMetric as ReportChildMetrics[number]['childMetric'],
              groupAnswersData,
              standardRef
            )
          : childMetric,
      })) as AssessableMetrics[number]['childrenMetrics'];
    updatedMetric = {
      ...updatedMetric,
      childrenMetrics: updatedChildren as AssessableMetrics[number]['childrenMetrics'],
    };
  }

  return updatedMetric;
};

export const addSubsidiaryTags = (
  metric: ReportMetric,
  subsidiaryAssessments: SubsidiaryAssessmentsType[],
  standardRef: string
) => {
  return isFullMetricOnSubLevel(metric as unknown as MaybeHasChild)
    ? addReportMaterialMetricTags(metric, subsidiaryAssessments ?? [], standardRef)
    : metric;
};

export const useHideShowMetric = () => {
  const [upsertMetric, { loading }] = useUpsertMaterialMetricsMutation();

  const hideShowMetric = useCallback(
    (metric: AggregatedMetricsTableData, standardId: string, isMetricHidden: boolean) => {
      if (
        metric.metric.childrenMetrics?.length &&
        metric.metric.metricType !== QuestionType.Decimal_
      ) {
        const mapChildren = (
          m: EsrsReportMetric['metric']['childrenMetrics'][number]
        ): Esrs_MaterialMetric_Insert_Input_[] => {
          return m.childMetric?.childrenMetrics?.length
            ? [
                ...m.childMetric.childrenMetrics?.flatMap((child) =>
                  mapChildren(child as EsrsReportMetric['metric']['childrenMetrics'][number])
                ),
                {
                  id: m.childMetric.materialMetrics[0].id,
                  metricRef: m.childMetric.reference,
                  materialStandardId: standardId,
                  isHidden: !isMetricHidden,
                },
              ]
            : [
                {
                  isHidden: !isMetricHidden,
                  id: m.childMetric?.materialMetrics[0]?.id,
                  materialStandardId: standardId,
                  metricRef: m.childMetric?.reference,
                },
              ];
        };

        const objects = [
          ...metric.metric.childrenMetrics.flatMap((child) => mapChildren(child)),
          {
            id: metric.metric.materialMetrics[0].id,
            metricRef: metric.metric.reference,
            materialStandardId: standardId,
            isHidden: !isMetricHidden,
          },
        ];

        return upsertMetric({
          variables: {
            objects: objects,
          },
        });
      } else {
        return upsertMetric({
          variables: {
            objects: {
              id: metric.metric.materialMetrics[0].id,
              metricRef: metric.metric.reference,
              materialStandardId: standardId,
              isHidden: !isMetricHidden,
            },
          },
        });
      }
    },
    [upsertMetric]
  );
  return { hideShowMetric, loading };
};

export const useIsMetricHidden = (metricRef: string, assessmentId: string) => {
  const { data, loading } = useGetMetricReportStatusSubscription({
    variables: {
      esrsAssessmentId: assessmentId,
    },
  });
  const resultMap = useMemo(() => {
    return data?.esrs_MaterialMetric.reduce(
      (acc, { metricRef: currentRef, isHidden }) => {
        acc[currentRef] = isHidden;
        return acc;
      },
      {} as { [key: string]: boolean }
    );
  }, [data]);

  const isHidden = useMemo(() => {
    return resultMap?.[metricRef] ?? false;
  }, [resultMap, metricRef]);

  return { isHidden, loading };
};

export const useIsDrHidden = (metricRefs: string[], assessmentId?: string) => {
  const { data, loading } = useGetMetricReportStatusSubscription({
    variables: {
      esrsAssessmentId: assessmentId,
    },
    skip: !assessmentId,
  });
  const resultMap = useMemo(() => {
    return data?.esrs_MaterialMetric.reduce(
      (acc, { metricRef: currentRef, isHidden }) => {
        acc[currentRef] = isHidden;
        return acc;
      },
      {} as { [key: string]: boolean }
    );
  }, [data]);

  const isHidden = useMemo(() => {
    return metricRefs.every((ref) => resultMap?.[ref] ?? false);
  }, [resultMap, metricRefs]);

  return { isHidden, loading };
};

export const useESRSReportStructure = (assessmentId?: string) => {
  const { framework = '' } = useParams();
  const { data: esrsAssessmentData, loading: assessmentDetailsLoading } =
    useGetEsrsAssessmentShortDetailsQuery({
      variables: {
        esrsAssessmentId: assessmentId,
      },
      skip: !assessmentId,
    });

  const isCollectOnly = esrsAssessmentData?.assessment?.collectOnly ?? false;

  const { data: parentAssessmentData, loading: parentAssessmentDataLoading } =
    useGetRequiredGroupMaterialMetricsQuery({
      variables: {
        parentAssessmentId: esrsAssessmentData?.assessment?.parentAssessmentId,
      },
      skip: !esrsAssessmentData?.assessment?.parentAssessmentId,
    });

  const groupMaterialMetrics = parentAssessmentData?.esrs_MaterialMetric ?? []
  const groupMaterialMetricsRefs = groupMaterialMetrics.map((mm) => mm.metricRef) ?? [];
  const groupMaterialStandardsRefs =
    uniq(groupMaterialMetrics.flatMap((mm) => mm.materialStandard.standardRef)) ?? [];

  const { data, loading } = useGetEsrsReportStructure({
    assessmentId: assessmentId ?? '',
    groupMetrics: groupMaterialMetricsRefs,
    groupStandards: groupMaterialStandardsRefs,
    isCollectOnly,
  });

  const { data: generalDrDisclosures, loading: generalDrLoading } =
    useGetReportGeneralDisclosureRequirementsQuery({
      variables: {
        assessmentId: assessmentId,
      },
      skip: !assessmentId,
    });

  const categoriesData = uniqBy(
    data?.flatMap((std) => std.standard?.category),
    'reference'
  ).sort((a, b) => ORDER_OF_CATEGORIES?.[a.title] - ORDER_OF_CATEGORIES?.[b.title]);
  const standardsData = data?.map((std) => std.standard);

  const structure: (StructureNode & { type?: string })[][][] = useMemo(() => {
    return (categoriesData ?? []).map((category) => {
      return [
        [
          {
            title: `${category.title === 'Environment' ? 'Environmental' : category.title} information`,
            key: category.reference,
            reference: category.reference,
            description: null,
            isHidden: false,
          },
        ],
        ...(
          standardsData?.filter((std) => std.category.reference === category.reference) ?? []
        ).map((std) => {
          const requirements = uniq(
            std.disclosureRequirementGroups
              .flatMap((drG) => {
                return std.reference === 'ESRS-2'
                  ? (generalDrDisclosures?.DisclosureRequirement ?? [])
                  : drG.requirements;
              })
              .sort((a, b) => a.reference.localeCompare(b.reference)) ?? []
          );
          const isMFStandard = mfStandardRefs.includes(std.reference);
          return [
            {
              title: `${framework !== FrameworkRefs.vsme ? std.reference.replace(/-/g, ' ') : ''} ${std.title}`,
              key: std.reference,
              reference: std.reference,
              isHidden: requirements.every(
                (req) => (req.notHiddenCount.aggregate?.count ?? 0) === 0
              ),
            },

            ...requirements
              .sort((a, b) => a.order - b.order)
              .map((dr) => {
                const isEntitySpecific = dr?.additionalTypes?.some(
                  (type) => type.additionalTypeRef === 'Entity-specific'
                );
                return {
                  title:
                    isEntitySpecific || isMFStandard
                      ? `${dr.title}`
                      : `${dr.reference} ${dr.title}`,
                  key: dr.reference,
                  reference: dr.reference,
                  isHidden: (dr.notHiddenCount.aggregate?.count ?? 0) === 0,
                  type: dr.type,
                };
              }),
          ];
        }),
      ];
    });
  }, [data]);

  const standards = useMemo(() => {
    return structure.flatMap((cat, index) =>
      cat.slice(1).map((std) => ({
        title: std[0].title,
        reference: std[0].reference,
        disclosureRequirementRefs: std.slice(1),
        category: `${toRomanNumeral(index + 1)}. ${cat[0][0].title}`,
      }))
    );
  }, [structure]);

  return {
    structure,
    standards,
    groupMaterialMetricsRefs,
    loading: loading || generalDrLoading || assessmentDetailsLoading || parentAssessmentDataLoading,
  };
};

// Get the type returned by getMetricData
export type MetricDataType = ReturnType<typeof getMetricData>;

export const getMetricData = (
  metric: GetReportMetricsPerDisclosureQuery_['EsrsMetric'][number]['materialMetrics'][number],
  standardId: string,
  standardRef: string,
  reportingUnitsAnswers: GetAnswersForMetricsOnCompanyLevelQuery_ | undefined,
  groupAnswers: GetAnswersForMetricsOnGroupLevelQuery_ | undefined,
  isGroup: boolean,
  buReportingUnitId: string,
  companyReportingUnitId: string,
  allMdrmAnswers: GetCompanyLevelMdrmAnswersQuery_['esrs_DatapointMdrm'] | undefined,
  currencyConversionData?: GetAssessmentCurrencyConversionQuery_['esrs_AssessmentCurrencyConversion']
) => {
  const metricObject = {
    ...metric.metric,
    childrenMetrics: metric.childrenMetrics,
    materialMetrics: [metric],
  };

  const metricWithTags = addSubsidiaryTags(
    metricObject,
    groupAnswers?.EsrsAssessment_by_pk?.subsidiaryAssessments ?? [],
    standardRef
  );

  const companyLevelRU = reportingUnitsAnswers?.EsrsAssessment_by_pk?.reportingUnits.find(
    (ru) => ru.isCompanyLevel
  );

  const answers = companyLevelRU?.answers ?? [];

  const answer = answers.find((ans) => ans.metricRef === metric.metricRef);

  const answerDP = answer?.datapoints?.find((dp) => !!dp.value);

  const textAnswer = answerDP?.value;

  const factValueAnswer = answerDP?.factValue;

  const datapoint = getLatestUpdated(answer?.datapoints);
  const textBefore = datapoint?.metadata?.before?.text;
  const textAfter = datapoint?.metadata?.after?.text;
  const isBeforeVisible = datapoint?.metadata?.before?.isVisible === true && !!textBefore;
  const isAfterVisible = datapoint?.metadata?.after?.isVisible === true && !!textAfter;
  const isMdrmDisclosed =
    answer?.datapoints && answer?.datapoints.length > 0
      ? answer?.datapoints.filter((dp) => dp.datapointTags.length === 0)?.[0]?.isMdrmDisclosed
      : undefined;

  const materialMetrics = groupAnswers?.EsrsAssessment_by_pk?.subsidiaryAssessments.flatMap(
    (company) =>
      company.materialStandards
        .find((ms) => ms.standardRef === standardRef)
        ?.materialMetrics.find((mm) => mm?.metricRef === metric.metricRef)
  );

  const nestedMetric = getNestedRows({
    metric: metricWithTags as AssessableMetrics[number],
    materialStandardId: standardId,
    isGroup,
    materialMetrics: !isFullMetricOnGroupLevel(metricWithTags as MaybeHasChild)
      ? (materialMetrics as GroupMaterialMetrics)
      : undefined,
  });

  const metricResult = getMetricResult({
    metric: metricWithTags as AssessableMetrics[number],
    nestedMetric,
    isGroup,
    reportingUnitsAnswers,
    groupAnswers,
    standardRef,
    materialStandardId: standardId,
    currencyConversionData: currencyConversionData,
  });

  const optedOutAnswers = companyLevelRU?.optedOutAnswers.map((a) => a.metricRef) ?? [];

  const result = filterOptedOutRows(metricResult, optedOutAnswers);

  const mdrmAnswers = getMetricMdrmAnswers(metricObject, allMdrmAnswers);

  return {
    metric: {
      ...metric,
      materialMetricTags: (metricWithTags?.materialMetrics?.[0].materialMetricTags ??
        metric.materialMetricTags) as ReportQueryMetric['materialMetrics'][number]['materialMetricTags'],
    },
    textAnswer: sanitizeText(
      metric.metric.metricType !== QuestionType.Decimal_ ? textAnswer : undefined
    ),
    factValue: factValueAnswer,
    tableData: result,
    reportingUnitId:
      metric.dataCollection === DataCollectionLevel.company
        ? companyReportingUnitId
        : buReportingUnitId,
    completed: !!(
      textAnswer ||
      (result.result?.Year && metric.metric.metricType === QuestionType.Decimal_)
    ),
    mdrmAnswers: mdrmAnswers,
    isBeforeVisible,
    isAfterVisible,
    textBefore: sanitizeText(textBefore),
    textAfter: sanitizeText(textAfter),
    isMdrmDisclosed,
    hasOptedOut: optedOutAnswers.some((metricRef) => metricRef === metric.metricRef),
  };
};

export const useReportAnswers = (assessmentId?: string) => {
  const { isGroup } = useCompanyType();
  const { reportingUnitAssessments } = useAssessmentReportingUnits(assessmentId);

  const [companyReportingUnitId, firstBUReportingUnitId] = useMemo(
    () => [
      reportingUnitAssessments.find((ru) => ru.isCompanyLevel)?.id ?? '',
      reportingUnitAssessments.filter((ru) => !ru.isCompanyLevel)?.[0]?.id ?? '',
    ],
    [reportingUnitAssessments]
  );

  const { data: reportingUnitsAnswers, loading: reportingUnitLoading } =
    useGetAnswersForMetricsOnCompanyLevelQuery({
      variables: {
        esrsAssessmentId: assessmentId,
      },
      fetchPolicy: 'cache-first',
      skip: !assessmentId,
    });

  const { data: subAnswersEnvironmentData, loading: loadingEnvironmentAnswers } =
    useGetSubAnswersEnvironmentQuery({
      variables: { esrsAssessmentId: assessmentId, environmentDisclosures: environmentDRRefs },
      fetchPolicy: 'cache-first',
      skip: !isGroup || !assessmentId,
    });

  const { data: subAnswersOtherData, loading: loadingOtherAnswers } = useGetSubAnswersOtherQuery({
    variables: { esrsAssessmentId: assessmentId, environmentDisclosures: environmentDRRefs },
    fetchPolicy: 'cache-first',
    skip: !isGroup || !assessmentId,
  });

  const { data: subMaterialMetricsData, loading: loadingSubMaterialMetrics } =
    useGetSubMaterialMetricsOnGroupLevelQuery({
      variables: { esrsAssessmentId: assessmentId },
      fetchPolicy: 'cache-first',
      skip: !isGroup || !assessmentId,
    });

  const groupAnswers = useMemo(() => {
    const reportingUnits =
      subAnswersEnvironmentData?.EsrsAssessment_by_pk?.subsidiaryAssessments?.map((assessment) => ({
        ...assessment,
        reportingUnits: assessment.reportingUnits?.map((rua) => ({
          ...rua,
          answers: [
            ...rua.answers,
            ...(subAnswersOtherData?.EsrsAssessment_by_pk?.subsidiaryAssessments
              ?.find((sa) => sa.id === assessment.id)
              ?.reportingUnits?.find((ru) => ru.id === rua.id)?.answers ?? []),
          ],
        })),
      })) ?? [];
    const data = {
      EsrsAssessment_by_pk: {
        ...subMaterialMetricsData?.EsrsAssessment_by_pk,
        id: subMaterialMetricsData?.EsrsAssessment_by_pk?.id ?? '',
        subsidiaryAssessments:
          subMaterialMetricsData?.EsrsAssessment_by_pk?.subsidiaryAssessments?.map((a) => ({
            ...a,
            reportingUnits:
              reportingUnits?.find((assessment) => assessment.id === a.id)?.reportingUnits ?? [],
          })) ?? [],
      },
    };
    return data ?? [];
  }, [subAnswersEnvironmentData, subAnswersOtherData, subMaterialMetricsData]);

  const { data: mdrmAnswersData, loading: mdrmAnswersLoading } = useGetCompanyLevelMdrmAnswersQuery(
    {
      variables: {
        reportingUnitId: companyReportingUnitId,
      },
      fetchPolicy: 'cache-first',
      skip: !companyReportingUnitId,
    }
  );

  const allMdrmAnswers = useMemo(
    () => mdrmAnswersData?.esrs_DatapointMdrm ?? [],
    [mdrmAnswersData]
  );

  const reportAnswers: ReportAnswers = {
    reportingUnitsAnswers,
    groupAnswers,
    allMdrmAnswers,
  };

  return {
    reportAnswers,
    loading: isGroup
      ? loadingEnvironmentAnswers ||
        loadingOtherAnswers ||
        loadingSubMaterialMetrics ||
        mdrmAnswersLoading
      : reportingUnitLoading || mdrmAnswersLoading,
    companyReportingUnitId,
    firstBUReportingUnitId,
  };
};

export const simplifyNarrativeMetrics = (
  metrics: EsrsReportMetric[]
): SimplifiedReportNarrativeMetric[] => {
  const metricMap = new Map<string, EsrsReportMetric>();

  metrics.forEach((narrativeMetric) => {
    metricMap.set(narrativeMetric.metric.metricRef, narrativeMetric);
  });

  // Children of assessable parents
  const childrenOfAssessableParents = new Set<string>();
  metrics.forEach((metric) => {
    if (metric.metric.metric.isAssessable) {
      metric.metric.childrenMetrics?.forEach((child) => {
        if (child.childMetric?.reference) {
          childrenOfAssessableParents.add(child.childMetric.reference);
        }
      });
    }
  });

  function buildSimplifiedMetric(metric: EsrsReportMetric): SimplifiedReportNarrativeMetric {
    return {
      materialMetric: metric.metric,
      textAnswer: metric.textAnswer,
      children:
        (metric.metric.childrenMetrics
          ?.map((child) => {
            const childMetric = metricMap.get(child.childMetric?.reference ?? '');
            return childMetric ? buildSimplifiedMetric(childMetric) : null;
          })
          .filter(Boolean) as SimplifiedReportNarrativeMetric[]) ?? [],
      isBeforeVisible: metric.isBeforeVisible,
      isAfterVisible: metric.isAfterVisible,
      textBefore: metric.textBefore,
      textAfter: metric.textAfter,
      isLocked: metric.isLocked,
      // For children quantitative metrics
      tableData: metric.tableData,
    };
  }

  return metrics
    .filter(
      (m) =>
        m.metric.metric.metricType !== QuestionType_Enum_.Decimal_ &&
        m.metric.metric.isAssessable &&
        !childrenOfAssessableParents.has(m.metric.metric.reference)
    )
    .map((m) => buildSimplifiedMetric(m));
};

export const useFilterDrMetrics = (
  isPreview: boolean,
  drMetrics?: GetReportMetricsPerDisclosureQuery_
) => {
  const { framework = '' } = useParams();
  const materialMetrics = useMemo(
    () => drMetrics?.EsrsMetric?.flatMap((metric) => metric.materialMetrics) ?? [],
    [drMetrics]
  );

  const isEsrs = useMemo(() => framework === FrameworkRefs.esrs, [framework]);

  const sortMetricsByFramework = (metrics: typeof materialMetrics) => {
    return metrics.sort((a, b) => {
      const aFramework = a.metric.requirement.frameworkRef;
      const bFramework = b.metric.requirement.frameworkRef;

      if (aFramework === framework && bFramework !== framework) return -1;
      if (aFramework !== framework && bFramework === framework) return 1;

      return 0;
    });
  };

  const sortedMaterialMetrics = sortMetricsByFramework(materialMetrics);

  const hardcodedMetricRefs = isEsrs ? [...E1_6_Target_Metrics, ...hardcodedMetrics] : [];

  const [parentMetrics, childMetrics] = useMemo(
    () => [
      sortedMaterialMetrics
        .filter(
          (met) =>
            !met.metric.parentMetrics.length ||
            // Also include child assessable numeric metrics, unless they are included in hardcoded tables or they have narrative parents
            (met.metric.metricType === QuestionType.Decimal_ &&
              met.metric.isAssessable &&
              met.metric.parentMetrics[0].parentMetric.metricType === QuestionType_Enum_.Decimal_ &&
              !hardcodedMetricRefs.includes(met.metric.reference))
        )
        .filter((m) => (isPreview ? !m.isHidden : true)),
      sortedMaterialMetrics
        .filter((met) => met.metric.parentMetrics.length)
        .filter((m) => (isPreview ? !m.isHidden : true)),
    ],
    [drMetrics, sortedMaterialMetrics]
  );

  const filteredMetrics = useMemo(
    () =>
      parentMetrics.map((metric) =>
        metric.metric.metricType === QuestionType.Decimal_
          ? filterQualitativeChildren(metric)
          : metric
      ),
    [parentMetrics]
  );

  return { filteredMetrics, materialMetrics, childMetrics };
};

export const useFirstViewportEntry = (
  observerOptions: IntersectionObserverInit,
  ref: MutableRefObject<HTMLDivElement | null>
) => {
  const [entered, setEntered] = useState(false);
  const observer = useRef(
    new IntersectionObserver(([entry]) => setEntered(entry.isIntersecting), observerOptions)
  );

  useEffect(() => {
    const element = ref?.current;
    const ob = observer.current;
    if (entered) {
      ob.disconnect();
      return;
    }
    if (element && !entered) ob.observe(element);
    return () => ob.disconnect();
  }, [entered, ref]);
  return entered;
};

export const getMilestoneValue = (target: TargetFieldsFragment_, year: number) => {
  const milestone = target.milestones.find((m) => m.year === year);
  const baseline = target.keyResults.find((kr) => kr.reportingUnitId === null);

  const milestoneResult = milestone?.milestoneResults.find(
    (result) => result.reportingUnitId === null
  );

  const hasValue =
    !!milestoneResult?.value && baseline?.baseline !== undefined && baseline?.baseline !== 'null';

  const milestoneValue = hasValue
    ? Number(baseline?.baseline) !== 0
      ? percentageToNumber(milestoneResult.value, Number(baseline?.baseline))
      : milestoneResult.value
    : undefined;

  return milestoneValue;
};

// Hooks for processing metrics in report preview - used for table cleanups
export const useProcessedDrMetrics = ({
  drMetrics,
  isPreview,
  standardRef,
  isGroupOwner,
  reportAnswers,
  currencyConversionData,
}: {
  drMetrics: GetReportMetricsPerDisclosureQuery_ | undefined;
  isPreview: boolean;
  standardRef: string;
  isGroupOwner?: boolean;
  reportAnswers: ReportAnswers;
  currencyConversionData?: GetAssessmentCurrencyConversionQuery_['esrs_AssessmentCurrencyConversion'];
}) => {
  const { esrsAssessmentId = '' } = useParams();
  const { filteredMetrics, childMetrics } = useFilterDrMetrics(isPreview, drMetrics);

  const { allMdrmAnswers, groupAnswers, reportingUnitsAnswers } = reportAnswers;

  const { reportingUnitAssessments } = useAssessmentReportingUnits(esrsAssessmentId);

  const [companyReportingUnitId, firstBUReportingUnitId] = useMemo(
    () => [
      reportingUnitAssessments.find((ru) => ru.isCompanyLevel)?.id ?? '',
      reportingUnitAssessments.filter((ru) => !ru.isCompanyLevel)?.[0]?.id ?? '',
    ],
    [reportingUnitAssessments]
  );

  const processMetrics = (metrics: typeof filteredMetrics, uniqueRowRefs: string[]) =>
    metrics.map((metric) => {
      const isNarrative = metric.metric.metricType !== QuestionType.Decimal_;

      const metricData = getMetricData(
        metric,
        metric.materialStandardId,
        standardRef ?? '',
        reportingUnitsAnswers,
        groupAnswers,
        isGroupOwner ?? false,
        isNarrative ? companyReportingUnitId : '',
        isNarrative ? firstBUReportingUnitId : '',
        isNarrative ? [] : allMdrmAnswers,
        currencyConversionData
      );

      return {
        ...metric,
        metricData: {
          ...metricData,
          tableData: isNarrative
            ? metricData.tableData
            : processReportTableData(metricData.tableData, uniqueRowRefs),
        },
      };
    });

  const [processedMetrics, processedChildMetrics] = useMemo(() => {
    // Unique refs used to store unique rows across table
    const uniqueRowRefs: string[] = [];

    const filtered = processMetrics(filteredMetrics, uniqueRowRefs);
    const children = processMetrics(childMetrics, uniqueRowRefs);

    return [filtered, children];
  }, [filteredMetrics, childMetrics]);

  return {
    processedMetrics,
    processedChildMetrics,
  };
};
